import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Download, Upload, Loader2, ShieldCheck, Database, Mail, Cloud, PlayCircle, Calendar,
} from "lucide-react";

/**
 * AdminBackupCard
 * ----------------
 * One-click DB backup / restore. Sits at the top of the admin > Businesses
 * tab so the founder can grab a JSON snapshot before major operations
 * (forks, Places imports, mass edits) and restore into a fresh DB later.
 *
 * Endpoints:
 *   GET  /api/admin/backup/summary  — collection counts (for the display)
 *   GET  /api/admin/backup/export   — JSON download of all whitelisted rows
 *   POST /api/admin/backup/import   — upload snapshot (upserts by natural key)
 */
export default function AdminBackupCard() {
  const [summary, setSummary] = useState({
    collections: [],
    schema_version: null,
    last_run: null,
    drive_configured: false,
    email_recipients: [],
  });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [restoreReport, setRestoreReport] = useState(null);
  const fileInputRef = useRef(null);

  const loadSummary = async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/backup/summary`,
        { withCredentials: true },
      );
      setSummary(data || { collections: [] });
    } catch {
      // silent
    }
  };

  useEffect(() => { loadSummary(); }, []);

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/backup/run-now`,
        {},
        { withCredentials: true },
      );
      const emailOk = data.email?.ok;
      const driveOk = data.drive?.ok;
      const driveSkipped = data.drive?.skipped;
      let msg = `Backup ran (${data.total_rows} rows). `;
      msg += emailOk ? "Email sent ✓" : "Email failed";
      if (driveOk) msg += " · Drive uploaded ✓";
      else if (!driveSkipped) msg += " · Drive failed";
      toast[emailOk ? "success" : "error"](msg);
      await loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run-now failed");
    } finally {
      setRunningNow(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/admin/backup/export`,
        { withCredentials: true, responseType: "blob" },
      );
      // Trigger download from the blob response.
      const blob = new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `tbc-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(
      `Restore from "${file.name}"?\n\n` +
      `This will UPSERT every row into the current database. Existing rows ` +
      `with the same ID will be OVERWRITTEN with values from the backup.\n\n` +
      `Continue?`
    )) {
      e.target.value = "";
      return;
    }

    setImporting(true);
    setRestoreReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/admin/backup/import`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setRestoreReport(data.report || []);
      const inserted = (data.report || []).reduce((s, r) => s + (r.inserted || 0), 0);
      const updated = (data.report || []).reduce((s, r) => s + (r.updated || 0), 0);
      toast.success(`Restored — ${inserted} new + ${updated} updated`);
      await loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <Card className="border-tbc-gold/40" data-testid="admin-backup-card">
      <CardHeader>
        <CardTitle className="font-heading text-tbc-navy flex items-center gap-2">
          <Database className="w-5 h-5 text-tbc-gold" />
          Backup &amp; Restore
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          One-click safety net. Download a full JSON snapshot of every business,
          product, submission &amp; curated list — or upload a snapshot to
          restore into a fresh database (e.g. after a fork).
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Weekly schedule + auto-backup status */}
        <div
          className="rounded-lg border border-tbc-navy/10 bg-white p-4"
          data-testid="admin-backup-schedule"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-tbc-gold" />
                <p className="font-heading text-tbc-navy text-sm">
                  Auto-backup: <strong>Every Sunday 06:00 UTC</strong>
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-emerald-50 text-emerald-800"
                  data-testid="admin-backup-email-status"
                >
                  <Mail className="w-3 h-3" /> Email → {summary.email_recipients?.[0] || "hello@theblkcommunity.com"}
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] ${
                    summary.drive_configured
                      ? "bg-emerald-50 text-emerald-800"
                      : "bg-amber-50 text-amber-800"
                  }`}
                  data-testid="admin-backup-drive-status"
                >
                  <Cloud className="w-3 h-3" />
                  {summary.drive_configured
                    ? "Google Drive → connected"
                    : "Google Drive → not connected"}
                </span>
              </div>
              {summary.last_run && (
                <p className="text-[11px] text-gray-500 mt-2" data-testid="admin-backup-last-run">
                  Last run: <span className="font-mono">{new Date(summary.last_run.ran_at).toLocaleString()}</span>
                  {" · "}{summary.last_run.total_rows} rows
                  {" · "}{summary.last_run.email?.ok ? "email ✓" : "email failed"}
                  {summary.last_run.drive?.ok && " · drive ✓"}
                </p>
              )}
            </div>
            <Button
              size="sm"
              onClick={handleRunNow}
              disabled={runningNow}
              className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
              data-testid="admin-backup-run-now-btn"
            >
              {runningNow ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4 mr-2" />
              )}
              Send Backup Now
            </Button>
          </div>

          {!summary.drive_configured && (
            <details className="mt-3 text-[12px] text-gray-600" data-testid="admin-backup-drive-setup">
              <summary className="cursor-pointer text-tbc-navy font-medium">
                How to hook up Google Drive (one-time, ~5 minutes)
              </summary>
              <ol className="list-decimal ml-5 mt-2 space-y-1 leading-relaxed">
                <li>Open <strong>Google Cloud Console</strong> → create a project (or pick an existing one) → enable <strong>Google Drive API</strong>.</li>
                <li>IAM &amp; Admin → <strong>Service Accounts</strong> → create one → open it → <strong>Keys → Add key → Create new key → JSON</strong>. Download the JSON file.</li>
                <li>Open Google Drive → create a folder (e.g. <em>TBC Backups</em>) → right-click → <strong>Share</strong> → paste the service account&apos;s email (looks like <code>...@...iam.gserviceaccount.com</code>) → give <strong>Editor</strong> access.</li>
                <li>Grab the folder ID from the folder URL (the string after <code>/folders/</code>).</li>
                <li>Send me the JSON file contents + folder ID and I&apos;ll paste them into the server env as <code>GOOGLE_DRIVE_SA_JSON</code> and <code>GOOGLE_DRIVE_FOLDER_ID</code>. Sunday backups will start landing in that folder automatically.</li>
              </ol>
            </details>
          )}
        </div>

        {/* Live counts per collection */}
        <div className="rounded-lg bg-tbc-cream/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-tbc-gold" />
            <p className="text-xs uppercase tracking-widest text-tbc-navy/70 font-semibold">
              What&apos;s in your DB right now
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="backup-summary-grid">
            {summary.collections.map((row) => (
              <div key={row.collection} className="bg-white rounded-md px-3 py-2 border border-gray-100">
                <p className="font-heading text-2xl text-tbc-navy" data-testid={`backup-count-${row.collection}`}>
                  {row.count}
                </p>
                <p className="text-[11px] uppercase tracking-wider text-gray-500">
                  {row.collection.replace(/_/g, " ")}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy"
            data-testid="admin-backup-export-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download Backup (.json)
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleRestore}
            className="hidden"
            data-testid="admin-backup-restore-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="admin-backup-restore-btn"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Restore From Backup…
          </Button>
        </div>

        {restoreReport && (
          <div
            className="rounded-lg border border-green-200 bg-green-50 p-4"
            data-testid="admin-backup-restore-report"
          >
            <p className="text-sm font-semibold text-green-900 mb-2">
              Restore complete
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-green-900/70">
                  <th className="py-1">Collection</th>
                  <th className="py-1 text-right">Inserted</th>
                  <th className="py-1 text-right">Updated</th>
                  <th className="py-1 text-right">Skipped</th>
                </tr>
              </thead>
              <tbody>
                {restoreReport.map((r) => (
                  <tr key={r.collection} className="border-t border-green-100">
                    <td className="py-1 font-mono text-green-900">{r.collection}</td>
                    <td className="py-1 text-right text-green-800">{r.inserted}</td>
                    <td className="py-1 text-right text-green-800">{r.updated}</td>
                    <td className="py-1 text-right text-green-800/70">{r.skipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[11px] text-gray-500 leading-relaxed">
          <strong>Tip:</strong> Download a backup before every deploy or fork.
          Restore is idempotent — upserts by business ID, product ID, etc. Users,
          orders, and payment data are intentionally excluded from the snapshot.
        </p>
      </CardContent>
    </Card>
  );
}
